import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { VfxShardBatch } from "../../core/batches/vfx_shard_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { randomRange } from "../../../common/common_funcs.js";

export enum IceSplinterType
{
  CRACK,
  BREAK
}

export class VfxIceSplintersTask implements IVfxTask, IController
{
  // --- Crack Constants ---
  private static readonly CRACK_BURST_COUNT       : number      = 3;
  private static readonly CRACK_LIFESPAN          : number      = 0.8;
  private static readonly CRACK_MIN_SPEED         : number      = 2.2;
  private static readonly CRACK_MAX_SPEED         : number      = 3.8;
  private static readonly CRACK_MIN_SCALE         : number      = 0.2;
  private static readonly CRACK_MAX_SCALE         : number      = 0.3;

  // --- Break / Shatter Constants ---
  private static readonly BREAK_BASE_COUNT        : number      = 4;
  private static readonly BREAK_LIFESPAN          : number      = 0.6;
  private static readonly BREAK_MIN_SPEED         : number      = 2;
  private static readonly BREAK_MAX_SPEED         : number      = 3;
  private static readonly BREAK_MIN_SCALE         : number      = 0.2;
  private static readonly BREAK_MAX_SCALE         : number      = 0.4;

  // --- Shared Physics & Visuals ---
  private static readonly GRAVITY                 : number      = 18.0;
  private static readonly RESTITUTION             : number      = 0.28;
  private static readonly FLOOR_Y                 : number      = 0.0;
  private static readonly FADE_START              : number      = 0.6;
  private static readonly SHRINK_START            : number      = 1.0;

  private static readonly ICE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterIce1,
    ResourceID.SplinterIce2,
    ResourceID.SplinterIce3,
    ResourceID.SplinterIce4,
    ResourceID.SplinterIce5,
  ];

  public is_active       : boolean = true;
  private position       : THREE.Vector3;
  private type           : IceSplinterType;
  private bounds_size?   : THREE.Vector3;
  private start_time     : number;
  private has_spawned    : boolean = false;
  private total_lifespan : number;

  public constructor(
    position: THREE.Vector3,
    type: IceSplinterType = IceSplinterType.BREAK,
    bounds_size?: THREE.Vector3
  )
  {
    this.position       = position.clone();
    this.type           = type;
    this.bounds_size    = bounds_size?.clone();
    this.start_time     = Vfx3DRuntime.current_time;
    this.total_lifespan = type === IceSplinterType.CRACK
      ? VfxIceSplintersTask.CRACK_LIFESPAN
      : VfxIceSplintersTask.BREAK_LIFESPAN;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      if ( this.type === IceSplinterType.CRACK )
      {
        this.spawnCrackSplinters();
      }
      else
      {
        this.spawnBreakSplinters();
      }
    }

    if ( age >= this.total_lifespan )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();

  private spawnCrackSplinters(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxShardBatch );
    const count = VfxIceSplintersTask.CRACK_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * Math.PI * 2 + randomRange( -0.2, 0.2 );
      const spawn_dist = randomRange( 0, 0.12 );

      VfxIceSplintersTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + randomRange( 0.05, 0.15 )
      );

      const speed = randomRange(
        VfxIceSplintersTask.CRACK_MIN_SPEED,
        VfxIceSplintersTask.CRACK_MAX_SPEED
      );
      const elevation = randomRange( 0.2, 0.6 );

      VfxIceSplintersTask.scratch_vel.set(
        Math.cos( angle ) * speed,
        ( Math.sin( angle ) * 0.4 + elevation ) * speed,
        randomRange( 0.4, 1.2 ) * speed
      );

      const scale = randomRange(
        VfxIceSplintersTask.CRACK_MIN_SCALE,
        VfxIceSplintersTask.CRACK_MAX_SCALE
      );

      const res_id = VfxIceSplintersTask.ICE_SPLINTER_IDS[
        Math.floor( Math.random() * VfxIceSplintersTask.ICE_SPLINTER_IDS.length )
      ];
      const spin_dir = VfxIceSplintersTask.scratch_vel.x >= 0 ? 1 : -1;
      const spin_speed = randomRange( 5.0, 11.0 );

      batch.spawnShard(
        res_id,
        VfxIceSplintersTask.scratch_pos,
        VfxIceSplintersTask.scratch_vel,
        VfxIceSplintersTask.GRAVITY,
        scale,
        randomRange( 0, Math.PI * 2 ),
        spin_dir * spin_speed,
        this.start_time,
        VfxIceSplintersTask.CRACK_LIFESPAN * randomRange( 0.85, 1.15 ),
        VfxIceSplintersTask.FLOOR_Y,
        VfxIceSplintersTask.FADE_START,
        VfxIceSplintersTask.SHRINK_START,
        0.0,
        VfxIceSplintersTask.RESTITUTION
      );
    }
  }

  private spawnBreakSplinters(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxShardBatch );
    const count = this.bounds_size
      ? Math.round( VfxIceSplintersTask.BREAK_BASE_COUNT * ( Math.max( this.bounds_size.x, this.bounds_size.y ) / 0.32 ) )
      : VfxIceSplintersTask.BREAK_BASE_COUNT;
    const final_count = THREE.MathUtils.clamp( count, 4, 8 );

    const half_x = this.bounds_size ? this.bounds_size.x * 0.4 : 0.15;
    const half_y = this.bounds_size ? this.bounds_size.y * 0.4 : 0.15;
    const half_z = this.bounds_size ? this.bounds_size.z * 0.4 : 0.15;

    for ( let i = 0; i < final_count; i++ )
    {
      const angle = ( i / final_count ) * Math.PI * 2 + randomRange( -0.25, 0.25 );

      VfxIceSplintersTask.scratch_pos.set(
        this.position.x + randomRange( -half_x, half_x ),
        this.position.y + randomRange( -half_y, half_y ),
        this.position.z + randomRange( -half_z, half_z )
      );

      const speed = randomRange(
        VfxIceSplintersTask.BREAK_MIN_SPEED,
        VfxIceSplintersTask.BREAK_MAX_SPEED
      );
      const elevation = randomRange( 0.5, 1.8 );

      VfxIceSplintersTask.scratch_vel.set(
        Math.cos( angle ) * speed,
        Math.sin( angle ) * speed * 0.6 + elevation,
        randomRange( -0.8, 1.6 ) * speed
      );

      const scale = randomRange(
        VfxIceSplintersTask.BREAK_MIN_SCALE,
        VfxIceSplintersTask.BREAK_MAX_SCALE
      );

      const res_id = VfxIceSplintersTask.ICE_SPLINTER_IDS[
        Math.floor( Math.random() * VfxIceSplintersTask.ICE_SPLINTER_IDS.length )
      ];
      const spin_dir = VfxIceSplintersTask.scratch_vel.x >= 0 ? 1 : -1;
      const spin_speed = randomRange( 6.0, 14.0 );

      batch.spawnShard(
        res_id,
        VfxIceSplintersTask.scratch_pos,
        VfxIceSplintersTask.scratch_vel,
        VfxIceSplintersTask.GRAVITY,
        scale,
        randomRange( 0, Math.PI * 2 ),
        spin_dir * spin_speed,
        this.start_time,
        VfxIceSplintersTask.BREAK_LIFESPAN * randomRange( 0.85, 1.15 ),
        VfxIceSplintersTask.FLOOR_Y,
        VfxIceSplintersTask.FADE_START,
        VfxIceSplintersTask.SHRINK_START,
        0.0,
        VfxIceSplintersTask.RESTITUTION
      );
    }
  }
}
